import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as api from '../../../../../api/generated';
import ListItemCreateButton from '../../../../../components/custom/ListItemCreateButton';
import { useFetchedValue } from '../../../../../components/hooks/useFetched';
import { usePageableGrid } from '../../../../../components/hooks/usePageableGrid';
import { Grid, GridColumn } from '../../../../../components/KendoReact/Grid';
import KendoLoading from '../../../../../components/spinners/KendoLoading';
import { useWindow } from '../../../../../components/windows/WindowContext';
import AdministrationActionCell from '../../components/AdministrationActionCell';
import ConnectionStatusCell from '../components/ConnectionStatusCell';
import TerminalNameCell from '../components/TerminalNameCell';
import TerminalCreateModalWindow from './TerminalCreateModalWindow';
import TerminalEditModalWindow from './TerminalEditModalWindow';

const getTerminals = async (organizationInformationId: number, skip: number, take: number) =>
    api.administration.getTerminals({
        organizationInformationId,
        skip,
        take
    });

const DeviceTerminals: FC = () => {
    const { organizationInformationId } = useParams<'organizationInformationId'>();

    const { openWindow } = useWindow();
    const [gridPageProps] = usePageableGrid([5, 10, 15, 20, 25], 15);
    const [isLoading, setIsLoading] = useState(false);
    const [terminals, refetchTerminals, , , isTerminalsLoading] = useFetchedValue(
        getTerminals,
        true,
        parseInt(organizationInformationId.toString()),
        gridPageProps.skip,
        gridPageProps.take
    );

    const openTerminalEditModalWindow = async (terminalId: number) => {
        try {
            TerminalEditModalWindow.defaultProps = {
                componentProps: {
                    terminalId: terminalId,
                    organizationInformationId: parseInt(organizationInformationId.toString()),
                    refetchTerminals: refetchTerminals
                }
            };
            openWindow({
                title: GetTitleByType(terminalId),
                component: TerminalEditModalWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const GetTitleByType = (terminalId: number): string => {
        const terminal = terminals.data.find((e) => e.id === terminalId);

        if (terminal != null && !terminal.isVirtual) {
            return 'Terminalo redagavimas';
        } else {
            return 'Virtualaus terminalo redagavimas';
        }
    };

    const openTerminalCreateModalWindow = async () => {
        try {
            TerminalCreateModalWindow.defaultProps = {
                componentProps: {
                    refetchTerminals: refetchTerminals
                }
            };
            openWindow({
                title: 'Virtualaus terminalo kūrimas',
                component: TerminalCreateModalWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    if (isLoading) return <KendoLoading />;

    const deleteTerminal = async (id: number) => {
        try {
            setIsLoading(true);
            await api.administration.deleteTerminal(id);
            refetchTerminals();
            toast.success('Įrenginys sėkmingai ištrintas');
        } catch (ex) {
            toast.error('Įrenginio ištrinti nepavyko, sistemos klaida');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <ListItemCreateButton openCreateModal={openTerminalCreateModalWindow} />
            <Grid className="mt-2" {...gridPageProps} data={terminals} isLoading={isTerminalsLoading}>
                <GridColumn
                    width="200px"
                    title="Veiksmai"
                    cell={(c) => (
                        <AdministrationActionCell
                            {...c}
                            fetchData={refetchTerminals}
                            deleteItem={deleteTerminal}
                            editItem={openTerminalEditModalWindow}
                        />
                    )}
                />
                <GridColumn
                    minResizableWidth={100}
                    width={300}
                    title="Pavadinimas"
                    cell={(c) => <TerminalNameCell terminalName={c.dataItem.name} isVirtual={c.dataItem.isVirtual} {...c} />}
                />
                <GridColumn minResizableWidth={100} width={150} title="Modelis" field="model" />

                <GridColumn minResizableWidth={50} width={100} title="Statusas" cell={(c) => <ConnectionStatusCell {...c} />} />
                <GridColumn minResizableWidth={100} width={150} title="Unikalus raktas" field="key" />
                <GridColumn minResizableWidth={100} title="Fizinė lokacija" field="locationName" />
            </Grid>
        </div>
    );
};

export default DeviceTerminals;
